import { AccountType } from '../shared/types'
import {
    Step1ValueModel,
    Step4ValueModel,
    Step5ValueModel,
} from '../shared/CustomerFormSteps/types'
import { Step2ValueModel, Step3ValueModel } from './types'
import { Section } from '../shared/Summary/types'
import { ISubmitCampsiteRegistrationBody } from "@apps/Shared/Services/CompanyRegistrations/interfaces";

export function getSubmitCampsiteRegistrationBody(
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel &
        Step5ValueModel
): ISubmitCampsiteRegistrationBody {
    const body: ISubmitCampsiteRegistrationBody = {
        /* Step 1 */
        organizationNumber: values.organizationNumber,
        /* Step 2 */
        newOwnerForExistingStore: values.changeOwner === 'true' ? true : false,
        newOwnerForExistingStoreChangeDate: values.changeOwnerDate,
        name: values.legalName,
        hasFTax: values.registeredForFTax === 'true' ? true : false,
        hasVAT: values.registeredForVAT === 'true' ? true : false,
        vatNumber: values.vatNumber,
        donationReceiverName: values.donateName,
        donationReceiverOrganizationNumber: values.donateOrganisationNumber,
        donationReceiverAccountType: values.donateAccountType,
        donationReceiverAccountNumber:
            values.donateAccountType === AccountType.Bg
                ? values.donateBg
                : values.donatePg,
        accountType: values.accountType,
        accountNumber:
            values.accountType === AccountType.Bg ? values.bg : values.pg,
        ownerPostalAddress: {
            street1: values.careOf,
            street2: values.streetBox,
            zipCode: values.zipCode,
            city: values.city,
        },
        /* Step 3 */
        customerSiteName: values.campsiteName,
        municipalityCode: values.municipality
            ? Number(values.municipality.value)
            : -1,
        visitingAddress: values.visitingAddress
            ? {
                  street1: values.visitingAddress.streetAddress,
                  zipCode: values.visitingAddress.zipCode,
                  city: values.visitingAddress.city,
              }
            : {},
        visitingAddressIsAlsoDeliveryAddress:
            (values.visitingAddress &&
                !!values.visitingAddress.isAlsoDeliveryAddress) ||
            false,
        postalAddress:
            values.visitingAddress &&
            !!values.visitingAddress.isAlsoDeliveryAddress
                ? {
                      street1: values.visitingAddress.streetAddress,
                      zipCode: values.visitingAddress.zipCode,
                      city: values.visitingAddress.city,
                  }
                : {
                      street1: values.postalAddress.streetAddress,
                      zipCode: values.postalAddress.zipCode,
                      city: values.postalAddress.city,
                  },
        deliveryAddress:
            values.visitingAddress &&
            !!values.visitingAddress.isAlsoDeliveryAddress
                ? {
                      street1: values.visitingAddress.streetAddress,
                      zipCode: values.visitingAddress.zipCode,
                      city: values.visitingAddress.city,
                  }
                : {
                      street1: values.deliveryAddress.streetAddress2,
                      street2: values.deliveryAddress.streetAddress1,
                      zipCode: values.deliveryAddress.zipCode,
                      city: values.deliveryAddress.city,
                  },
        message: values.message,
        /* Step 4 */
        contactPerson: values.contactPerson
            ? {
                  firstname: values.contactPerson.firstname,
                  lastname: values.contactPerson.lastname,
                  email: values.contactPerson.email,
                  mobileNumber: values.contactPerson.mobile,
                  phoneNumber: values.contactPerson.phone,
              }
            : {},
        /* Step 5 */
        GDPRConsentText: values.termsText,
    }

    if (values.heard && values.heard.value) {
        body.howTheyHeardOfUsId = values.heard.value
    }

    return body
}

export function getSubmitSkiResortRegistrationBody(
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel &
        Step5ValueModel
): ISubmitCampsiteRegistrationBody {
    return getSubmitCampsiteRegistrationBody(values)
}

export function getSubmitWorkPlaceRegistrationBody(
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel &
        Step5ValueModel
): ISubmitCampsiteRegistrationBody {
    return getSubmitCampsiteRegistrationBody(values)
}

export function getSections(
    t: Function,
    tPrefix: string,
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel
): Section[] {
    function formatBooleanString(value: any) {
        return value === 'true'
            ? t('global.yes').toUpperCase()
            : t('global.no').toUpperCase()
    }

    return [
        {
            title: t(`companyRegistration.customer.${tPrefix}.stepLabel.step2`),
            subsections: [
                {
                    title: '',
                    items: [
                        {
                            title: t(
                                'companyRegistration.organisationNumberLabel'
                            ),
                            value: values.organizationNumber,
                        },
                        {
                            title: t('companyRegistration.customer.legalName'),
                            value: values.legalName,
                        },
                        {
                            title: t(
                                'companyRegistration.customer.changeOwnerLabel'
                            ),
                            value: formatBooleanString(values.changeOwner),
                        },
                        {
                            title: t(
                                'companyRegistration.customer.changeOwnerDate'
                            ),
                            value: values.changeOwnerDate,
                            hidden: values.changeOwner !== 'true',
                        },
                        {
                            title: t('companyRegistration.fTaxLabel'),
                            value: formatBooleanString(
                                values.registeredForFTax
                            ),
                        },
                        {
                            title: t(
                                'companyRegistration.registeredForVatLabel'
                            ),
                            value: formatBooleanString(values.registeredForVAT),
                        },
                        {
                            title: t('companyRegistration.customer.vatNumber'),
                            value: values.vatNumber,
                            hidden: values.registeredForVAT !== 'true',
                        },
                        {
                            title: t(
                                'companyRegistration.customer.accountType'
                            ),
                            value:
                                values.accountType === AccountType.Bg
                                    ? values.bg
                                    : values.pg,
                        },
                    ],
                },
                {
                    title: t('companyRegistration.customer.campsite.donate'),
                    items: [
                        {
                            title: t(
                                'companyRegistration.customer.campsite.donateName'
                            ),
                            value: values.donateName,
                        },
                        {
                            title: t(
                                'companyRegistration.customer.campsite.donateOrganisationNumberLabel'
                            ),
                            value: values.donateOrganisationNumber,
                        },
                        {
                            title: t(
                                'companyRegistration.customer.accountType'
                            ),
                            value:
                                values.donateAccountType === AccountType.Bg
                                    ? values.donateBg
                                    : values.donatePg,
                        },
                    ],
                    hidden: values.charity !== 'true',
                },
                {
                    title: t('companyRegistration.customer.postalAddress'),
                    items: [
                        {
                            title: t('companyRegistration.customer.careOf'),
                            value: values.careOf,
                        },
                        {
                            title: t('companyRegistration.customer.streetBox'),
                            value: values.streetBox,
                        },
                        {
                            title: t('companyRegistration.zipCode'),
                            value: values.zipCode,
                        },
                        {
                            title: t('companyRegistration.city'),
                            value: values.city,
                        },
                    ],
                },
            ],
        },
        {
            title: t(`companyRegistration.customer.${tPrefix}.stepLabel.step3`),
            subsections: [
                {
                    title: '',
                    items: [
                        {
                            title: t(
                                `companyRegistration.customer.${tPrefix}.campsiteName`
                            ),
                            value: values.campsiteName,
                        },
                        {
                            title: t(
                                'companyRegistration.customer.municipality'
                            ),
                            value:
                                (values.municipality &&
                                    values.municipality.label) ||
                                '',
                        },
                    ],
                },
                {
                    title: t('companyRegistration.customer.visitingAddress'),
                    items: [
                        {
                            title: t('companyRegistration.streetAddress'),
                            value:
                                values.visitingAddress &&
                                values.visitingAddress.streetAddress,
                        },
                        {
                            title: t('companyRegistration.zipCode'),
                            value:
                                values.visitingAddress &&
                                values.visitingAddress.zipCode,
                        },
                        {
                            title: t('companyRegistration.city'),
                            value:
                                values.visitingAddress &&
                                values.visitingAddress.city,
                        },
                    ],
                },
                values.visitingAddress &&
                values.visitingAddress.isAlsoDeliveryAddress
                    ? {
                          title: t(
                              'companyRegistration.customer.campsite.postalAddress'
                          ),
                          items: [
                              {
                                  title: t('companyRegistration.streetAddress'),
                                  value:
                                      values.visitingAddress &&
                                      values.visitingAddress.streetAddress,
                              },
                              {
                                  title: t('companyRegistration.zipCode'),
                                  value:
                                      values.visitingAddress &&
                                      values.visitingAddress.zipCode,
                              },
                              {
                                  title: t('companyRegistration.city'),
                                  value:
                                      values.visitingAddress &&
                                      values.visitingAddress.city,
                              },
                          ],
                      }
                    : {
                          title: t(
                              'companyRegistration.customer.campsite.postalAddress'
                          ),
                          items: [
                              {
                                  title: t(
                                      'companyRegistration.customer.campsite.postalAddressStreetAddress'
                                  ),
                                  value:
                                      values.postalAddress &&
                                      values.postalAddress.streetAddress,
                              },
                              {
                                  title: t('companyRegistration.zipCode'),
                                  value:
                                      values.postalAddress &&
                                      values.postalAddress.zipCode,
                              },
                              {
                                  title: t('companyRegistration.city'),
                                  value:
                                      values.postalAddress &&
                                      values.postalAddress.city,
                              },
                          ],
                      },
                values.visitingAddress &&
                values.visitingAddress.isAlsoDeliveryAddress
                    ? {
                          title: t(
                              'companyRegistration.customer.campsite.deliveryAddress'
                          ),
                          items: [
                              {
                                  title: t('companyRegistration.streetAddress'),
                                  value:
                                      values.visitingAddress &&
                                      values.visitingAddress.streetAddress,
                              },
                              {
                                  title: t('companyRegistration.zipCode'),
                                  value:
                                      values.visitingAddress &&
                                      values.visitingAddress.zipCode,
                              },
                              {
                                  title: t('companyRegistration.city'),
                                  value:
                                      values.visitingAddress &&
                                      values.visitingAddress.city,
                              },
                          ],
                      }
                    : {
                          title: t(
                              'companyRegistration.customer.campsite.deliveryAddress'
                          ),
                          items: [
                              {
                                  title: t(
                                      'companyRegistration.customer.campsite.deliveryAddressStreetAddress1'
                                  ),
                                  value:
                                      values.deliveryAddress &&
                                      values.deliveryAddress.streetAddress1,
                              },
                              {
                                  title: t(
                                      'companyRegistration.customer.campsite.deliveryAddressStreetAddress2'
                                  ),
                                  value:
                                      values.deliveryAddress &&
                                      values.deliveryAddress.streetAddress2,
                              },
                              {
                                  title: t('companyRegistration.zipCode'),
                                  value:
                                      values.deliveryAddress &&
                                      values.deliveryAddress.zipCode,
                              },
                              {
                                  title: t('companyRegistration.city'),
                                  value:
                                      values.deliveryAddress &&
                                      values.deliveryAddress.city,
                              },
                          ],
                      },
                {
                    title: t('companyRegistration.customer.messageForRp'),
                    items: [
                        {
                            title: t('companyRegistration.customer.message'),
                            value: values.message,
                        },
                    ],
                },
                {
                    title: '',
                    items: [
                        {
                            title: t(
                                'companyRegistration.customer.campsite.howDidYouHearAboutUs'
                            ),
                            value: (values.heard && values.heard.label) || '',
                        },
                    ],
                },
            ],
        },
        {
            title: t(`companyRegistration.customer.${tPrefix}.stepLabel.step4`),
            subsections: [
                {
                    title: t('companyRegistration.customer.contactPerson'),
                    slim: true,
                    items: [
                        {
                            title: t('global.firstname'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.firstname,
                        },
                        {
                            title: t('global.lastname'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.lastname,
                        },
                        {
                            title: t('global.email'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.email,
                        },
                        {
                            title: t('global.mobileNumber'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.mobile,
                        },
                        {
                            title: t('global.phoneNumber'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.phone,
                        },
                    ],
                },
            ],
        },
    ]
}